import React, { useMemo, useState } from "react";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "@remix-run/react";
import { classNames } from "~/utils";

type HeaderSearchType = "medien" | "fachartikel";

interface ISearchBoxHeader {
  query?: string;
  setSearchQuery: (value: string) => void;
  onSubmit?: () => void;
}

const searchOptions: Array<{ value: HeaderSearchType; label: string }> = [
  { value: "medien", label: "Medien" },
  { value: "fachartikel", label: "Fachbeiträge" },
];

const SearchBoxHeader: React.FC<ISearchBoxHeader> = ({
  query = "",
  setSearchQuery,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState<HeaderSearchType>("medien");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const selectedLabel = useMemo(() => {
    const selected = searchOptions.find(
      (option) => option.value === activeType
    );
    return selected?.label ?? "Medien";
  }, [activeType]);

  const getActionByType = (type: HeaderSearchType, searchQuery: string) => {
    const encodedQuery = encodeURIComponent(searchQuery);
    if (type === "fachartikel") {
      return `/professional_article/main?q=${encodedQuery}`;
    }
    return `/search/main?q=${encodedQuery}`;
  };

  const handleSearch = () => {
    navigate(getActionByType(activeType, query));
    if (typeof onSubmit === "function") {
      onSubmit();
    }
  };

  return (
    <div className="w-full">
      <div className="flex w-full">
        <label
          htmlFor="header-search-input"
          className="sr-only mb-2.5 block text-sm font-medium text-gray-700"
        >
          Suche
        </label>

        <div className="relative">
          <button
            id="dropdown-button-search-type"
            type="button"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className="z-10 inline-flex shrink-0 items-center rounded-l-full border border-transparent bg-white px-4 py-3 text-sm font-medium leading-5 text-gray-700 shadow-[0_1px_3px_rgba(0,0,0,0.134479)] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-btnColor-secondary"
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
          >
            {selectedLabel}
            <ChevronDownIcon className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </button>

          {isDropdownOpen && (
            <div
              id="dropdown-search-type"
              className="absolute left-0 top-full z-10 mt-1 w-44 rounded-md border border-gray-300 bg-white shadow-lg"
            >
              <ul
                className="p-2 text-sm font-medium text-gray-600"
                aria-labelledby="dropdown-button-search-type"
              >
                {searchOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveType(option.value);
                        setIsDropdownOpen(false);
                      }}
                      className={classNames(
                        "inline-flex w-full items-center rounded-md p-2 text-left hover:bg-gray-100 hover:text-gray-900",
                        activeType === option.value
                          ? "bg-gray-100 text-gray-900"
                          : ""
                      )}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <input
          id="header-search-input"
          type="search"
          value={query}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              handleSearch();
            }
          }}
          className="block w-full border border-transparent bg-white px-3 py-3 text-sm text-gray-900 shadow-[0_1px_3px_rgba(0,0,0,0.134479)] placeholder:text-gray-500 focus:border-btnColor-secondary focus:outline-none focus:ring-0"
          placeholder={
            activeType === "fachartikel"
              ? "Suche nach Fachbeiträgen"
              : "Suche nach Medien"
          }
          required
        />

        <button
          type="button"
          onClick={handleSearch}
          className="inline-flex items-center rounded-r-full border border-transparent bg-btnColor-secondary px-4 py-3 text-sm font-medium leading-5 text-white hover:bg-btnColor-hoveSecondary focus:outline-none focus:ring-2 focus:ring-btnColor-secondary"
        >
          <MagnifyingGlassIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Suchen
        </button>
      </div>
    </div>
  );
};

export default SearchBoxHeader;
